// Exportação do consolidado para .xlsx de verdade (não CSV nem HTML disfarçado),
// com formatação: cabeçalho da marca, moeda em reais, percentuais, larguras,
// painel congelado, zebra e prejuízo em vermelho.

// O package.json do exceljs aponta "browser" para dist/exceljs.min.js, então o
// webpack já empacota o bundle de navegador — e o Node usa o build nativo.
import ExcelJS from 'exceljs'
import { numero, nomeDoMes } from './financeiroCalculos.js'

const COR = {
    marca: 'FF00695C',
    marcaClara: 'FFE0F2F1',
    cabecalho: 'FF00796B',
    zebra: 'FFF6FAF9',
    texto: 'FF14312D',
    apagado: 'FF6B8480',
    negativo: 'FFC62828',
    borda: 'FFD5E5E2',
    branco: 'FFFFFFFF'
}

const MOEDA = 'R$ #,##0.00'
const PERCENTUAL = '0.0%'
const INTEIRO = '#,##0'

function bordaFina(){
    return {
        top: { style: 'thin', color: { argb: COR.borda } },
        left: { style: 'thin', color: { argb: COR.borda } },
        bottom: { style: 'thin', color: { argb: COR.borda } },
        right: { style: 'thin', color: { argb: COR.borda } }
    }
}

function preencher(celula, cor){
    celula.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: cor } }
}

// Faixa de título da aba, no tom da marca.
function montarTitulo(planilha, titulo, subtitulo, colunas){
    planilha.mergeCells(1, 1, 1, colunas)
    const celulaTitulo = planilha.getCell(1, 1)
    celulaTitulo.value = titulo
    celulaTitulo.font = { name: 'Calibri', size: 16, bold: true, color: { argb: COR.branco } }
    celulaTitulo.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
    preencher(celulaTitulo, COR.marca)
    planilha.getRow(1).height = 30

    planilha.mergeCells(2, 1, 2, colunas)
    const celulaSubtitulo = planilha.getCell(2, 1)
    celulaSubtitulo.value = subtitulo
    celulaSubtitulo.font = { name: 'Calibri', size: 10, italic: true, color: { argb: COR.apagado } }
    celulaSubtitulo.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
    preencher(celulaSubtitulo, COR.marcaClara)
    planilha.getRow(2).height = 20
}

function montarCabecalhoTabela(planilha, linha, titulos){
    const linhaCabecalho = planilha.getRow(linha)

    titulos.forEach((titulo, indice) => {
        const celula = linhaCabecalho.getCell(indice + 1)
        celula.value = titulo
        celula.font = { name: 'Calibri', size: 11, bold: true, color: { argb: COR.branco } }
        celula.alignment = { vertical: 'middle', horizontal: indice === 0 ? 'left' : 'right' }
        preencher(celula, COR.cabecalho)
        celula.border = bordaFina()
    })

    linhaCabecalho.height = 22
    return linhaCabecalho
}

function escreverLinha(planilha, linha, valores, opcoes){
    const configuracao = opcoes || {}
    const linhaPlanilha = planilha.getRow(linha)

    valores.forEach((valor, indice) => {
        const celula = linhaPlanilha.getCell(indice + 1)
        celula.value = valor
        celula.border = bordaFina()
        celula.alignment = { vertical: 'middle', horizontal: indice === 0 ? 'left' : 'right' }
        celula.font = {
            name: 'Calibri',
            size: 11,
            bold: !!configuracao.negrito,
            color: { argb: COR.texto }
        }

        if(configuracao.formatos && configuracao.formatos[indice]){
            celula.numFmt = configuracao.formatos[indice]
        }

        if(configuracao.zebra){
            preencher(celula, COR.zebra)
        }

        // Prejuízo salta aos olhos.
        if(configuracao.colunasNegativas
            && configuracao.colunasNegativas.includes(indice)
            && numero(valor) < 0){
            celula.font = { ...celula.font, color: { argb: COR.negativo }, bold: true }
        }
    })

    return linhaPlanilha
}

// Bloco de indicadores do período, em pares rótulo/valor.
function montarBlocoIndicadores(planilha, linhaInicial, indicadores){
    let linha = linhaInicial

    const titulo = planilha.getCell(linha, 1)
    titulo.value = 'RESUMO DO PERÍODO'
    titulo.font = { name: 'Calibri', size: 11, bold: true, color: { argb: COR.marca } }
    linha += 1

    indicadores.forEach((indicador) => {
        const celulaRotulo = planilha.getCell(linha, 1)
        celulaRotulo.value = indicador.rotulo
        celulaRotulo.font = { name: 'Calibri', size: 11, color: { argb: COR.apagado } }
        celulaRotulo.border = bordaFina()
        preencher(celulaRotulo, COR.marcaClara)

        const celulaValor = planilha.getCell(linha, 2)
        celulaValor.value = indicador.valor
        celulaValor.numFmt = indicador.formato || MOEDA
        celulaValor.font = {
            name: 'Calibri',
            size: 11,
            bold: true,
            color: { argb: numero(indicador.valor) < 0 && indicador.destacarNegativo ? COR.negativo : COR.texto }
        }
        celulaValor.alignment = { horizontal: 'right' }
        celulaValor.border = bordaFina()
        preencher(celulaValor, COR.marcaClara)

        linha += 1
    })

    return linha
}

function abaConsolidado(pasta, dados){
    const { resumos, consolidado, periodo, subtitulo, nomeBarbearia } = dados
    const planilha = pasta.addWorksheet('Consolidado', {
        views: [{ state: 'frozen', ySplit: 0 }],
        pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
    })

    planilha.columns = [
        { width: 26 }, { width: 16 }, { width: 16 }, { width: 14 },
        { width: 14 }, { width: 16 }, { width: 16 }, { width: 16 }, { width: 12 }
    ]

    montarTitulo(
        planilha,
        `${nomeBarbearia} · Consolidado financeiro`,
        subtitulo,
        9
    )

    const indicadores = [
        { rotulo: 'Faturamento bruto', valor: numero(consolidado.faturamentoBruto) },
        { rotulo: 'Descontos', valor: numero(consolidado.descontos) },
        { rotulo: 'Taxas de cartão', valor: numero(consolidado.taxasCartao) },
        { rotulo: 'Comissões', valor: numero(consolidado.comissoes) },
        { rotulo: 'Despesas operacionais', valor: numero(consolidado.despesasOperacionais) },
        { rotulo: 'Despesas em aberto', valor: numero(consolidado.despesasEmAberto) },
        { rotulo: 'Lucro do período', valor: numero(consolidado.lucro), destacarNegativo: true },
        { rotulo: 'Margem líquida', valor: numero(consolidado.margem), formato: PERCENTUAL, destacarNegativo: true },
        { rotulo: 'Atendimentos recebidos', valor: numero(consolidado.quantidadeRecebidos), formato: INTEIRO },
        { rotulo: 'Média de lucro por mês', valor: numero(consolidado.mediaLucro), destacarNegativo: true }
    ]

    let linha = montarBlocoIndicadores(planilha, 4, indicadores) + 1

    const tituloTabela = planilha.getCell(linha, 1)
    tituloTabela.value = 'MÊS A MÊS'
    tituloTabela.font = { name: 'Calibri', size: 11, bold: true, color: { argb: COR.marca } }
    linha += 1

    const linhaCabecalho = linha
    montarCabecalhoTabela(planilha, linhaCabecalho, [
        'Mês', 'Atendimentos', 'Faturamento', 'Descontos', 'Taxas',
        'Comissões', 'Despesas operacionais', 'Lucro', 'Margem'
    ])
    linha += 1

    const formatos = [null, INTEIRO, MOEDA, MOEDA, MOEDA, MOEDA, MOEDA, MOEDA, PERCENTUAL]

    resumos.forEach((mes, indice) => {
        escreverLinha(planilha, linha, [
            nomeDoMes(mes.mes),
            numero(mes.quantidadeRecebidos),
            numero(mes.faturamentoBruto),
            numero(mes.descontos),
            numero(mes.taxasCartao),
            numero(mes.comissoes),
            numero(mes.despesasOperacionais),
            numero(mes.lucro),
            numero(mes.margem)
        ], { formatos, zebra: indice % 2 === 1, colunasNegativas: [7] })
        linha += 1
    })

    const linhaTotal = escreverLinha(planilha, linha, [
        'TOTAL',
        numero(consolidado.quantidadeRecebidos),
        numero(consolidado.faturamentoBruto),
        numero(consolidado.descontos),
        numero(consolidado.taxasCartao),
        numero(consolidado.comissoes),
        numero(consolidado.despesasOperacionais),
        numero(consolidado.lucro),
        numero(consolidado.margem)
    ], { formatos, negrito: true, colunasNegativas: [7] })

    linhaTotal.eachCell((celula) => {
        preencher(celula, COR.marcaClara)
        celula.border = { ...bordaFina(), top: { style: 'double', color: { argb: COR.marca } } }
    })

    // Congela tudo acima da primeira linha de dados e liga o filtro do cabeçalho.
    planilha.views = [{ state: 'frozen', ySplit: linhaCabecalho }]
    planilha.autoFilter = {
        from: { row: linhaCabecalho, column: 1 },
        to: { row: linhaCabecalho + resumos.length, column: 9 }
    }

    // O período pode ter meses sem lançamento; deixa isso explícito.
    if(periodo && periodo.mesesSemDados && periodo.mesesSemDados.length){
        linha += 2
        const aviso = planilha.getCell(linha, 1)
        aviso.value = `Sem totais salvos para: ${periodo.mesesSemDados.map(nomeDoMes).join(', ')}`
        aviso.font = { name: 'Calibri', size: 10, italic: true, color: { argb: COR.negativo } }
    }

    return planilha
}

function abaDetalhe(pasta, titulo, subtitulo, nomeColuna, itens, totalFaturamento){
    const planilha = pasta.addWorksheet(titulo, {
        pageSetup: { paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0 }
    })

    planilha.columns = [
        { width: 34 }, { width: 16 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 14 }
    ]

    montarTitulo(planilha, titulo, subtitulo, 6)

    const linhaCabecalho = 4
    montarCabecalhoTabela(planilha, linhaCabecalho, [
        nomeColuna, 'Atendimentos', 'Faturamento', 'Líquido gerado', 'Ticket médio', '% do total'
    ])

    const formatos = [null, INTEIRO, MOEDA, MOEDA, MOEDA, PERCENTUAL]
    let linha = linhaCabecalho + 1

    itens.forEach((item, indice) => {
        const faturamento = numero(item.faturamento)
        escreverLinha(planilha, linha, [
            item.nome,
            numero(item.quantidade),
            faturamento,
            numero(item.liquido),
            numero(item.quantidade) ? faturamento / numero(item.quantidade) : 0,
            totalFaturamento ? faturamento / totalFaturamento : 0
        ], { formatos, zebra: indice % 2 === 1 })
        linha += 1
    })

    const linhaTotal = escreverLinha(planilha, linha, [
        'TOTAL',
        itens.reduce((acc, item) => acc + numero(item.quantidade), 0),
        itens.reduce((acc, item) => acc + numero(item.faturamento), 0),
        itens.reduce((acc, item) => acc + numero(item.liquido), 0),
        '',
        totalFaturamento ? 1 : 0
    ], { formatos, negrito: true })

    linhaTotal.eachCell((celula) => {
        preencher(celula, COR.marcaClara)
        celula.border = { ...bordaFina(), top: { style: 'double', color: { argb: COR.marca } } }
    })

    planilha.views = [{ state: 'frozen', ySplit: linhaCabecalho }]
    return planilha
}

export async function gerarPastaConsolidado(dados){
    const pasta = new ExcelJS.Workbook()
    pasta.creator = dados.usuario || 'Controle financeiro'
    pasta.created = new Date()

    abaConsolidado(pasta, dados)

    abaDetalhe(
        pasta,
        'Por barbeiro',
        dados.subtitulo,
        'Barbeiro',
        dados.consolidado.porBarbeiro || [],
        numero(dados.consolidado.faturamentoBruto)
    )

    abaDetalhe(
        pasta,
        'Por categoria',
        dados.subtitulo,
        'Categoria de serviço',
        dados.consolidado.porCategoria || [],
        numero(dados.consolidado.faturamentoBruto)
    )

    return pasta
}

export async function gerarArquivoConsolidado(dados){
    const pasta = await gerarPastaConsolidado(dados)
    const buffer = await pasta.xlsx.writeBuffer()

    return new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
}

export function baixarArquivo(blob, nomeArquivo){
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = nomeArquivo
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Libera a memória do blob depois que o navegador iniciou o download.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
}
